import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';

import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ClienteService,
  SECTOR_TO_API,
} from '@core/services/domain-services';
import { NifService } from '@core/services/nif.service';
import {
  EnumService,
  TipoServicoOption,
} from '@shared/api/services/enum.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { calcularCobrancaContrato } from '@shared/utils/contrato-cobranca.util';

function fimAposInicio(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const inicio = group.get('inicioContrato')?.value;
  const fim = group.get('fimContrato')?.value;
  if (!inicio || !fim) return null;
  return new Date(fim) > new Date(inicio) ? null : { fimAnterior: true };
}

function validarModeloCobranca(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const modelo = group.get('modeloCobranca')?.value;
  if (!modelo) return null;
  if (modelo === 'fixo_mensal') {
    return Number(group.get('valorMensal')?.value) > 0
      ? null
      : { modeloIncompleto: true };
  }
  const valorUnitario = Number(group.get('valorUnitario')?.value) || 0;
  if (valorUnitario <= 0) return { modeloIncompleto: true };
  const qtdCtrl =
    modelo === 'por_efetivo'
      ? group.get('numEfetivos')
      : modelo === 'por_posto'
        ? group.get('numPostos')
        : group.get('numHoras');
  return Number(qtdCtrl?.value) > 0 ? null : { modeloIncompleto: true };
}

@Component({
  selector: 'app-cliente-novo',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    SelectModule,

    TooltipModule,
  ],

  templateUrl: './cliente-novo.component.html',
  styleUrl: './cliente-novo.component.scss',
})
export class ClienteNovoComponent implements OnInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('contratoFileInput')
  contratoFileInputRef!: ElementRef<HTMLInputElement>;

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly svc = inject(ClienteService);
  private readonly enumSvc = inject(EnumService);
  private readonly nifSvc = inject(NifService);
  private readonly msg = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  form!: FormGroup;
  contratoForm!: FormGroup;
  saving = false;

  get f() {
    return this.form.controls;
  }
  get cf() {
    return this.contratoForm.controls;
  }
  nifSearching = false;
  private nifAutoFilled = false;
  logoPreview: string | null = null;
  logoFile: File | null = null;
  contratoFile: File | null = null;
  criarContrato = false;

  readonly modeloCobrancas = [
    { label: 'Fixo Mensal', value: 'fixo_mensal' },
    { label: 'Por Efetivo', value: 'por_efetivo' },
    { label: 'Por Posto', value: 'por_posto' },
    { label: 'Por Hora', value: 'por_hora' },
  ];
  modeloCobranca: string = 'fixo_mensal';
  tiposServico: TipoServicoOption[] = [];

  readonly sectors = [
    { label: 'Banca e Finanças', value: 'banca' },
    { label: 'Petróleo e Gás', value: 'petroleo' },
    { label: 'Aviação e Aeroportos', value: 'aviacao' },
    { label: 'Telecomunicações', value: 'telecomunicacoes' },
    { label: 'Saúde', value: 'saude' },
    { label: 'Comércio e Retalho', value: 'comercio' },
    { label: 'Hotelaria e Turismo', value: 'hotelaria' },
    { label: 'Indústria e Manufatura', value: 'industria' },
    { label: 'Sector Público', value: 'publico' },
    { label: 'Construção', value: 'construcao' },
    { label: 'Outro', value: 'outro' },
  ];

  ngOnInit(): void {
    this.form = this.fb.group({
      nif: [
        '',
        [
          Validators.minLength(8),
          Validators.maxLength(20),
          Validators.pattern(/^[A-Za-z0-9]+$/),
        ],
      ],
      nome: ['', [Validators.required, Validators.minLength(2)]],
      sector: [null],
      website: [
        '',
        [
          Validators.pattern(
            /^(https?:\/\/)?(www\.)?[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,}(\/\S*)?$/,
          ),
        ],
      ],
      numTelefone: ['', [Validators.pattern(/^\+?[\d\s\-()]{9,20}$/)]],
      email: ['', [Validators.email]],
      nomeResponsavel: [''],
      endereco: [''],
    });

    this.contratoForm = this.fb.group(
      {
        tipoServico: [null, Validators.required],
        modeloCobranca: ['fixo_mensal', Validators.required],
        valorMensal: [null],
        numPostos: [0, [Validators.required, Validators.min(1)]],
        valorUnitario: [null],
        numEfetivos: [null],
        numHoras: [null],
        inicioContrato: [null, Validators.required],
        fimContrato: [null, Validators.required],
        dataFaturacao: [null],
      },
      { validators: [fimAposInicio, validarModeloCobranca] },
    );

    this.form
      .get('nif')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nif) => {
        if (this.nifAutoFilled) {
          this.nifAutoFilled = false;
          this.form.patchValue({ nome: '' });
        }

        if (this.isNifPessoaSingular(nif)) {
          this.form.patchValue({ sector: null }, { emitEvent: false });
        }
      });

    this.loadTiposServico();
  }

  private loadTiposServico(): void {
    this.enumSvc
      .getTipoServico()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (opcoes) => {
          this.tiposServico = opcoes;
        },
        error: () => {
          this.tiposServico = [];
        },
      });
  }

  isNifPessoaSingular(nif = this.form?.get('nif')?.value): boolean {
    return /^\d{9}[A-Za-z]{2}\d{3}$/.test((nif ?? '').trim());
  }

  get totalMensalCalculado(): number {
    const v = this.contratoForm?.value;
    if (!v) return 0;
    const vu = Number(v.valorUnitario) || 0;
    switch (this.modeloCobranca) {
      case 'por_efetivo':
        return vu * (Number(v.numEfetivos) || 0);
      case 'por_posto':
        return vu * (Number(v.numPostos) || 0);
      case 'por_hora':
        return vu * (Number(v.numHoras) || 0);
      default:
        return 0;
    }
  }

  onModeloChange(evt?: any): void {
    this.modeloCobranca = evt?.value ?? 'fixo_mensal';
    this.contratoForm.get('numPostos')?.setValue(0);
    this.contratoForm.get('valorMensal')?.setValue(null);
    this.contratoForm.get('valorUnitario')?.setValue(null);
    this.contratoForm.get('numEfetivos')?.setValue(null);
    this.contratoForm.get('numHoras')?.setValue(null);
  }

  get duracaoMeses(): number {
    const inicio = this.contratoForm.get('inicioContrato')?.value;
    const fim = this.contratoForm.get('fimContrato')?.value;
    if (!inicio || !fim) return 0;
    const d1 = new Date(inicio);
    const d2 = new Date(fim);
    return Math.max(
      0,
      (d2.getFullYear() - d1.getFullYear()) * 12 +
        (d2.getMonth() - d1.getMonth()),
    );
  }

  formatPhone(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 9);
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 3);
    if (digits.length > 3) formatted += ' ' + digits.slice(3, 6);
    if (digits.length > 6) formatted += ' ' + digits.slice(6, 9);
    input.value = formatted;
    this.form.get('numTelefone')?.setValue(formatted, { emitEvent: false });
  }

  fmtKz(v: number): string {
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  pesquisarNif(): void {
    const nif = this.form.get('nif')?.value?.trim();
    if (!nif) {
      this.msg.add({
        severity: 'warn',
        summary: 'NIF em falta',
        detail: 'Introduza um NIF antes de pesquisar.',
        life: 3000,
      });
      return;
    }
    this.nifSearching = true;
    this.nifSvc
      .lookup(nif)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.form.patchValue({ nome: c.nome });
          this.nifAutoFilled = true;
          this.msg.add({
            severity: 'success',
            summary: 'NIF encontrado',
            detail: `Dados preenchidos: ${c.nome}`,
            life: 3000,
          });
          this.nifSearching = false;
        },
        error: () => {
          this.msg.add({
            severity: 'info',
            summary: 'NIF não encontrado',
            detail: 'Preencha os dados manualmente.',
            life: 4000,
          });
          this.nifSearching = false;
        },
      });
  }

  triggerLogoInput(): void {
    this.fileInputRef?.nativeElement.click();
  }

  triggerContratoFileInput(): void {
    this.contratoFileInputRef?.nativeElement.click();
  }

  onContratoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) this.contratoFile = input.files[0];
  }

  removeContratoFile(event: Event): void {
    event.stopPropagation();
    this.contratoFile = null;
    if (this.contratoFileInputRef?.nativeElement) {
      this.contratoFileInputRef.nativeElement.value = '';
    }
  }

  formatBytes(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;

    const file = input.files[0];

    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.msg.add({
        severity: 'warn',
        summary: 'Formato não suportado',
        detail: 'Apenas imagens PNG ou JPG são aceites.',
        life: 4000,
      });
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);

    compressImageIfNeeded(file).then((compressed) => {
      this.logoFile = compressed;
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.msg.add({
        severity: 'error',
        summary: 'Erro de Validação',
        detail: 'Preencha todos os campos obrigatórios.',
        life: 4000,
      });
      return;
    }
    if (this.criarContrato && this.contratoForm.invalid) {
      this.contratoForm.markAllAsTouched();
      this.msg.add({
        severity: 'warn',
        summary: 'Contrato incompleto',
        detail: 'Preencha os campos obrigatórios do contrato.',
        life: 4000,
      });
      return;
    }
    this.saving = true;

    const fv = this.form.value;
    const nifPessoaSingular = this.isNifPessoaSingular(fv.nif);
    const fd = new FormData();
    fd.append('Nome', fv.nome ?? '');
    fd.append('Nif', fv.nif ?? '');
    if (nifPessoaSingular) fd.append('Bi', fv.nif);
    fd.append('NumTelefone', fv.numTelefone ?? '');
    fd.append('NomeResponsavel', fv.nomeResponsavel ?? '');
    fd.append('WebSite', fv.website ?? '');
    fd.append('Email', fv.email ?? '');
    fd.append('Endereco', fv.endereco ?? '');
    if (!nifPessoaSingular && fv.sector) {
      fd.append(
        'SetorAtividadeEmpresaEnum',
        SECTOR_TO_API[fv.sector] ?? fv.sector,
      );
    }
    if (this.logoFile) fd.append('Fotografia', this.logoFile);

    if (this.criarContrato) {
      const cv = this.contratoForm.value;
      const cobranca = calcularCobrancaContrato({
        modelo: cv.modeloCobranca,
        valorMensal: cv.valorMensal,
        valorUnitario: cv.valorUnitario,
        numEfetivos: cv.numEfetivos,
        numPostos: cv.numPostos,
        numHoras: cv.numHoras,
      });
      fd.append('Contratos[0].tipoServico', String(cv.tipoServico));
      fd.append(
        'Contratos[0].modeloCobranca',
        String(cobranca.modeloCobranca),
      );
      fd.append(
        'Contratos[0].valorUnitario',
        String(cobranca.valorUnitario),
      );
      fd.append('Contratos[0].quantidade', String(cobranca.quantidade));
      fd.append(
        'Contratos[0].descricaoFaturacao',
        cobranca.descricaoFaturacao,
      );
      fd.append('Contratos[0].ValorMensal', String(cobranca.valorMensal));
      fd.append('Contratos[0].NumPostos', String(cv.numPostos ?? 1));
      fd.append('Contratos[0].DataInicio', this.fmt(cv.inicioContrato));
      fd.append('Contratos[0].DataFim', this.fmt(cv.fimContrato));
      fd.append('Contratos[0].DataFaturacao', this.fmt(cv.dataFaturacao));
      if (this.contratoFile) {
        fd.append('Contratos[0].Ficheiro', this.contratoFile);
      }
    }

    this.svc
      .createWithFormData(fd)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const id = res?.id ?? res?.data?.id ?? 0;
          const detail = this.criarContrato
            ? 'Cliente e contrato criados com sucesso!'
            : 'Cliente criado com sucesso!';
          this.msg.add({
            severity: 'success',
            summary: 'Sucesso',
            detail,
            life: 3500,
          });
          this.saving = false;
          setTimeout(() => this.router.navigate(['/admin/clientes', id]), 1000);
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  private fmt(d: any): string {
    if (!d) return '';
    return d instanceof Date ? d.toISOString().split('T')[0] : String(d);
  }

  voltar(): void {
    this.router.navigate(['/admin/clientes']);
  }
}
